<template>
  <div>
    <v-card class="mx-auto my-4 mb-8" max-width="800" flat outlined>
      <v-container class="mx-4 my-4">
        <v-row class="mb-3" align="center">
          <h3>{{ $t("settings.personio.title") }}</h3>
          <div class="flex-grow-1"></div>
          <v-chip
            v-if="settings"
            :color="settings.connected ? 'success' : 'default'"
            small
            class="mr-4"
          >
            {{
              settings.connected
                ? $t("settings.personio.connected")
                : $t("settings.personio.notConnected")
            }}
          </v-chip>
        </v-row>
        <p class="grey--text text--darken-1 mb-0">
          {{ $t("settings.personio.description") }}
        </p>
      </v-container>

      <v-divider />

      <v-form ref="form" class="pa-6" lazy-validation>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.clientId"
              :label="$t('settings.personio.clientId')"
              :rules="
                !settings?.connected || form.clientSecret
                  ? [(v) => !!v || $t('general.fieldNeedsToBeFilled')]
                  : []
              "
              outlined
              dense
              :disabled="isReadOnlyUser"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.clientSecret"
              :label="$t('settings.personio.clientSecret')"
              :placeholder="
                settings && settings.connected
                  ? settings.clientSecretMasked
                  : ''
              "
              :rules="
                settings && settings.connected
                  ? []
                  : [(v) => !!v || $t('general.fieldNeedsToBeFilled')]
              "
              :hint="
                settings && settings.connected
                  ? $t('settings.personio.secretHint')
                  : ''
              "
              persistent-hint
              outlined
              dense
              :append-icon="showSecret ? 'mdi-eye-off' : 'mdi-eye'"
              :type="showSecret ? 'text' : 'password'"
              @click:append="showSecret = !showSecret"
              :disabled="isReadOnlyUser"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12">
            <v-combobox
              v-model="form.defaultTags"
              :items="tagItems"
              :label="$t('settings.personio.defaultTags')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              hide-selected
              outlined
              dense
              :hint="$t('settings.personio.defaultTagsHint')"
              persistent-hint
              :disabled="isReadOnlyUser"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="form.defaultLanguage"
              :items="languageItems"
              :label="$t('settings.personio.defaultLanguage')"
              item-text="name"
              item-value="value"
              clearable
              outlined
              dense
              hide-details="auto"
              :disabled="isReadOnlyUser"
            />
          </v-col>
          <v-col cols="12">
            <v-combobox
              v-model="form.allowedEmailDomains"
              :label="$t('settings.personio.allowedEmailDomains')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              outlined
              dense
              :delimiters="[',', ' ']"
              :hint="$t('settings.personio.allowedEmailDomainsHint')"
              persistent-hint
              :disabled="isReadOnlyUser"
            />
          </v-col>
          <v-col cols="12">
            <v-combobox
              v-model="form.disallowedEmailDomains"
              :label="$t('settings.personio.disallowedEmailDomains')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              outlined
              dense
              :delimiters="[',', ' ']"
              :hint="$t('settings.personio.disallowedEmailDomainsHint')"
              persistent-hint
              :disabled="isReadOnlyUser"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-checkbox
              v-model="form.countryTagsEnabled"
              :label="$t('settings.personio.countryTags')"
              hide-details
              class="mt-0 mb-2"
              :disabled="isReadOnlyUser"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="settings && settings.connected && settings.lastSyncedAt"
          class="mt-0"
        >
          <v-col cols="12">
            <div class="caption grey--text">
              {{ $t("settings.personio.lastSync") }}:
              {{ formatDate(settings.lastSyncedAt) }}
            </div>
          </v-col>
        </v-row>
      </v-form>

      <v-card-actions class="px-6 pb-6 pt-0">
        <v-btn
          v-if="settings && settings.connected && !isReadOnlyUser"
          color="error"
          text
          :loading="disconnecting"
          @click="disconnect"
        >
          {{ $t("settings.personio.disconnect") }}
        </v-btn>

        <v-btn
          v-if="settings && settings.connected && !isReadOnlyUser"
          outlined
          :loading="syncing"
          @click="syncNow"
        >
          {{ $t("settings.personio.syncNow") }}
        </v-btn>

        <div class="flex-grow-1" />

        <v-btn
          v-if="!isReadOnlyUser"
          color="primary"
          :loading="saving"
          @click="save"
        >
          {{
            settings && settings.connected
              ? $t("general.saveChanges")
              : $t("settings.personio.connect")
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "PersonioSettings",
  computed: {
    allEmployeeTags() {
      return this.$store.getters.allEmployeeTags || [];
    },
    tagItems() {
      return this.allEmployeeTags.map((t) => t.tag);
    },
    languageItems() {
      return [
        { name: "Deutsch", value: "GER" },
        { name: "English", value: "ENG" },
      ];
    },
  },
  data() {
    return {
      settings: null,
      form: {
        clientId: "",
        clientSecret: "",
        defaultTags: [],
        defaultLanguage: null,
        allowedEmailDomains: [],
        disallowedEmailDomains: [],
        countryTagsEnabled: false,
      },
      showSecret: false,
      saving: false,
      syncing: false,
      disconnecting: false,
    };
  },
  async created() {
    this.$store.dispatch("getAllEmployeeTags");
    await this.load();
  },
  methods: {
    async load() {
      this.settings = await this.$store.dispatch("getPersonioSettings");
      if (this.settings?.connected) {
        this.form.clientId = this.settings.clientId || "";
        this.form.clientSecret = "";
        this.form.defaultTags = this.settings.defaultTags || [];
        this.form.defaultLanguage = this.settings.defaultLanguage || null;
        this.form.allowedEmailDomains = this.settings.allowedEmailDomains || [];
        this.form.disallowedEmailDomains =
          this.settings.disallowedEmailDomains || [];
        this.form.countryTagsEnabled = !!this.settings.countryTagsEnabled;
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      const payload = {
        defaultTags: this.form.defaultTags,
        defaultLanguage: this.form.defaultLanguage,
        allowedEmailDomains: this.form.allowedEmailDomains,
        disallowedEmailDomains: this.form.disallowedEmailDomains,
        countryTagsEnabled: this.form.countryTagsEnabled,
      };

      if (this.form.clientSecret) {
        payload.clientId = this.form.clientId;
        payload.clientSecret = this.form.clientSecret;
      } else if (!this.settings?.connected) {
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("settings.personio.secretRequired")
        );
        return;
      }

      this.saving = true;
      try {
        const result = await this.$store.dispatch(
          "savePersonioSettings",
          payload
        );
        if (result) {
          this.$store.commit(
            "CREATE_SNACKBAR",
            this.$t("settings.personio.savedSuccess")
          );
          this.form.clientSecret = "";
          await this.load();
        }
      } finally {
        this.saving = false;
      }
    },

    async disconnect() {
      this.disconnecting = true;
      try {
        await this.$store.dispatch("deletePersonioSettings");
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("settings.personio.disconnected")
        );
        this.settings = null;
        this.form = {
          clientId: "",
          clientSecret: "",
          defaultTags: [],
          defaultLanguage: null,
          allowedEmailDomains: [],
          disallowedEmailDomains: [],
          countryTagsEnabled: false,
        };
      } finally {
        this.disconnecting = false;
      }
    },

    async syncNow() {
      this.syncing = true;
      try {
        const result = await this.$store.dispatch("triggerPersonioSync");
        if (result?.ok) {
          this.$store.commit(
            "CREATE_SNACKBAR",
            this.$t("settings.personio.syncStarted")
          );
        }
      } finally {
        this.syncing = false;
      }
    },

    formatDate(iso) {
      if (!iso) return "-";
      return new Date(iso).toLocaleString();
    },
  },
};
</script>
